import { useEffect, useRef } from "react";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";

import { authClient } from "../auth-client";
import { AccessDeniedView } from "../components/auth/AccessDeniedView";
import { InShellNotFound } from "../components/auth/InShellNotFound";
import { ViewerRolesProvider } from "../components/auth/ViewerRolesContext";
import { DashboardShell } from "../components/shell/DashboardShell";
import { navSections } from "../nav-config";
import { nubankTrpc } from "../trpc";

export const Route = createFileRoute("/_authed")({
  component: AuthedLayout,
  notFoundComponent: InShellNotFound,
});

function AuthedLayout() {
  const { data: session, isPending } = authClient.useSession();
  const accessQuery = nubankTrpc.accessState.useQuery(undefined, {
    enabled: !!session?.user,
  });
  const meQuery = nubankTrpc.me.useQuery(undefined, {
    enabled: !!session?.user && accessQuery.data?.isAuthorized === true,
  });
  const navigate = useNavigate();
  const posthog = usePostHog();
  const identifiedRef = useRef(false);

  useEffect(() => {
    if (!isPending && !session?.user) {
      void navigate({ to: "/login" });
    }
  }, [session, isPending, navigate]);

  useEffect(() => {
    if (session?.user && !identifiedRef.current) {
      identifiedRef.current = true;
      posthog.identify(session.user.id, {
        email: session.user.email,
        name: session.user.name ?? session.user.email,
      });
    }
  }, [session, posthog]);

  const isAuthLoading = isPending || !session?.user || accessQuery.isPending;

  if (isAuthLoading) {
    return (
      <DashboardShell
        sections={navSections}
        viewerRoles={{ isNuAdmin: false, isSuperadmin: false }}
        user={null}
        onSignOut={() => void authClient.signOut()}
        isLoading
      >
        {null}
      </DashboardShell>
    );
  }

  if (!accessQuery.data?.isAuthorized) {
    return <AccessDeniedView />;
  }

  const viewerRoles = {
    isNuAdmin: meQuery.data?.access.isNuAdmin ?? false,
    isSuperadmin: meQuery.data?.access.isSuperadmin ?? false,
  };

  return (
    <ViewerRolesProvider value={viewerRoles}>
      <DashboardShell
        sections={navSections}
        viewerRoles={viewerRoles}
        user={{
          name: session.user.name ?? session.user.email,
          email: session.user.email,
        }}
        onSignOut={() => {
          posthog.capture("user_signed_out");
          posthog.reset();
          void authClient.signOut();
        }}
      >
        <Outlet />
      </DashboardShell>
    </ViewerRolesProvider>
  );
}
